import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ShoppingCart, DollarSign, Monitor, Building2, BarChart3, Wrench } from "lucide-react";

interface WorkflowTemplate {
  id: string;
  title: string;
  description: string;
  industry: string;
  icon: React.ReactNode;
  goals: string[];
  config: {
    industry: string;
    dataSourceType: string;
    cleaningRules: string[];
    predictionGoals: string[];
  };
}

const templates: WorkflowTemplate[] = [
  {
    id: "ecommerce-sales",
    title: "E-commerce Weekly Sales",
    description: "Track revenue, conversion rates, and cart abandonment with automated weekly analysis.",
    industry: "E-commerce",
    icon: <ShoppingCart className="h-5 w-5" />,
    goals: ["Revenue forecasting", "Cart abandonment analysis", "Peak hours detection"],
    config: {
      industry: "ecommerce",
      dataSourceType: "csv",
      cleaningRules: ["remove_nulls", "normalize_currency", "deduplicate"],
      predictionGoals: ["revenue_forecast", "conversion_optimization"],
    },
  },
  {
    id: "finance-cashflow",
    title: "Finance Cash Flow Forecast",
    description: "Monitor cash flow ratios, ROI, and risk indicators with predictive modeling.",
    industry: "Finance",
    icon: <DollarSign className="h-5 w-5" />,
    goals: ["Cash flow prediction", "Risk assessment", "Expense optimization"],
    config: {
      industry: "finance",
      dataSourceType: "csv",
      cleaningRules: ["remove_nulls", "validate_numbers", "standardize_dates"],
      predictionGoals: ["cashflow_forecast", "risk_scoring"],
    },
  },
  {
    id: "tech-retention",
    title: "Tech User Retention",
    description: "Analyze user engagement, churn rates, and feature adoption across your platform.",
    industry: "Tech",
    icon: <Monitor className="h-5 w-5" />,
    goals: ["Churn prediction", "Feature adoption tracking", "Engagement scoring"],
    config: {
      industry: "tech",
      dataSourceType: "api",
      cleaningRules: ["remove_nulls", "normalize_percentages", "deduplicate"],
      predictionGoals: ["churn_prediction", "retention_optimization"],
    },
  },
  {
    id: "realestate-market",
    title: "Real Estate Market Analysis",
    description: "Track property valuations, market demand, and investment potential scores.",
    industry: "Real Estate",
    icon: <Building2 className="h-5 w-5" />,
    goals: ["Valuation trends", "Market demand forecasting", "Lead conversion analysis"],
    config: {
      industry: "realestate",
      dataSourceType: "csv",
      cleaningRules: ["remove_nulls", "validate_numbers", "standardize_addresses"],
      predictionGoals: ["valuation_forecast", "demand_prediction"],
    },
  },
  {
    id: "general-kpis",
    title: "General Business KPIs",
    description: "A universal template for tracking core business metrics and growth indicators.",
    industry: "General",
    icon: <BarChart3 className="h-5 w-5" />,
    goals: ["Growth tracking", "Performance benchmarks", "Trend analysis"],
    config: {
      industry: "general",
      dataSourceType: "csv",
      cleaningRules: ["remove_nulls", "deduplicate"],
      predictionGoals: ["growth_forecast"],
    },
  },
  {
    id: "custom",
    title: "Custom Workflow",
    description: "Start from scratch — configure your own data source, cleaning rules, and predictions.",
    industry: "Custom",
    icon: <Wrench className="h-5 w-5" />,
    goals: ["Fully customizable"],
    config: {
      industry: "",
      dataSourceType: "",
      cleaningRules: [],
      predictionGoals: [],
    },
  },
];

interface WorkflowTemplatesLibraryProps {
  onSelectTemplate?: (config: WorkflowTemplate["config"]) => void;
}

export const WorkflowTemplatesLibrary = ({ onSelectTemplate }: WorkflowTemplatesLibraryProps) => {
  const [search, setSearch] = useState("");

  const filtered = templates.filter(
    t =>
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.industry.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search templates..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(tpl => (
          <Card key={tpl.id} className="flex flex-col hover:border-primary/50 transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">{tpl.icon}</div>
                <Badge variant="secondary">{tpl.industry}</Badge>
              </div>
              <CardTitle className="text-base mt-3">{tpl.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{tpl.description}</p>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-end gap-3">
              <div className="flex flex-wrap gap-1.5">
                {tpl.goals.map(g => (
                  <Badge key={g} variant="outline" className="text-xs font-normal">
                    {g}
                  </Badge>
                ))}
              </div>
              <Button
                className="w-full"
                variant={tpl.id === "custom" ? "outline" : "default"}
                onClick={() => onSelectTemplate?.(tpl.config)}
              >
                {tpl.id === "custom" ? "Start from Scratch" : "Use Template"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
